import React, { useEffect, useState } from 'react';
import { Link, Redirect, useParams, useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { logout } from '../actions/registerUser';
import axios from 'axios';
import Footer from './Footer';
import Spinner from './Spinner';
import QrScan from './QrScan';
import ListItem from './ListItem';
import PaymentListItem from './PaymentListItem';
import LeadCaptureModal from './LeadCaptureModal';
import {
  DEFAULT_AVATAR,
  DEFAULT_COVER,
  handleAvatarError,
} from '../utils/imageFallback';
import { computeSocialDisplay, sortEntriesForDisplay } from '../utils/socialPlatforms';
import usePaymentAccordion from '../utils/usePaymentAccordion';
import { buildSupportWhatsAppUrl } from '../utils/support';

const getid = (hash) => {
  let index = hash.indexOf('-');
  let result = hash.slice(index + 1);
  return result;
};

const PublicProfile = ({ authh: { isAuth, loading }, logedUser }) => {
  const history = useHistory();
  const { id } = useParams();
  const [user, setuser] = useState();
  const [show, setshow] = useState('show1');
  const [error, seterror] = useState(null);
  // 'inactive' | 'notActivated' | null -- GET /current/:id's two distinct
  // 403 shapes, checked server-side in that order (deactivated wins over
  // unactivated), so a card that is both always reaches this component
  // as 'inactive'.
  const [gateReason, setGateReason] = useState(null);
  const [showLeadModal, setShowLeadModal] = useState(false);
  // Declared here, with the other hooks and above every early return
  // below (error, the two gate screens, the self-view redirect, the
  // spinner). A hook called after a conditional return runs on some
  // renders and not others, which React rejects outright.
  const { isOpen: isPaymentOpen, toggle: togglePayment } = usePaymentAccordion();
  const parsed = parseInt(getid(id), 10);

  // Deactivated or never-activated card: GET /current/:id returns 403
  // with no profile data at all -- distinguish both from a genuine
  // "no such user" 400/404 so all three can render differently below.
  const handleProfileFetchError = (err) => {
    const data = err.response && err.response.status === 403 && err.response.data;
    if (data && data.inactive) {
      setGateReason('inactive');
    } else if (data && data.notActivated) {
      setGateReason('notActivated');
    } else {
      seterror('not Found');
    }
  };

  useEffect(() => {
    (() => {
      if (!isNaN(parsed)) {
        axios
          .get(`/api/users/myusername/${id}`)
          .then((res) => {
            const user = res.data;
            // No userName set yet -- nothing to canonicalize to, so stay
            // on this lengthId URL and fetch the profile by it directly
            // (GET /current/:id falls back to lengthId) instead of
            // pushing to `/undefined`.
            if (user.userName) {
              history.push(`/${user.userName}`);
            } else {
              axios
                .get(`/api/users/current/${id}`)
                .then((user) => setuser(user.data))
                .catch(handleProfileFetchError);
            }
          })
          .catch(() => {
            axios
              .get(`/api/users/current/${id}`)
              .then((user) => setuser(user.data))
              .catch(handleProfileFetchError);
          });
      } else {
        axios
          .get(`/api/users/current/${id}`)
          .then((user) => setuser(user.data))
          .catch(handleProfileFetchError);
      }
    })();
  }, [id]);

  const handleClicks = async (entry) => {
    // Fire-and-forget: the public profile never displays click counts
    // (ListItem's isPublic=true hides them), so there is no local state
    // to update here -- just tell the server which entry was tapped. The
    // response is a 204 with no body by design; nothing is read back.
    //
    // entryId is what the server prefers, since one profile can carry
    // several entries on the same platform (two IBANs, two custom
    // links). name is still sent as a fallback for an entry derived from
    // the legacy `social` object, which has no _id of its own.
    try {
      await axios.post(
        `/api/users/update_clicks/${user._id}`,
        JSON.stringify({ entryId: entry && entry._id, name: entry && entry.platform }),
        { headers: { 'Content-type': 'application/json' } }
      );
    } catch (err) {}
  };

  if (error) return <Redirect to="/login" />;

  if (gateReason === 'inactive') {
    const supportMessage = `Hi, my Digital Contact Card (${id}) appears to be deactivated. Can you help me reactivate it?`;
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          padding: '40px 24px',
          backgroundColor: '#fff4e0',
        }}
      >
        {/* Deactivated, not expired. This gate fires on isActive ===
            false -- an admin turned the card off -- which has nothing to
            do with a term ending. It said "expired" until now, which
            misdescribed it to anyone scanning the card and invited
            "but I paid for five years" support contacts. It also had to
            stop saying it before real expiry ships and needs the word:
            AdminStatusControl already reserves an Expired state for
            that.

            Deliberately not "this card isn't active" -- the
            notActivated gate below is "this card isn't active yet", and
            two public gates one word apart are indistinguishable to the
            stranger reading them and ambiguous in a support ticket,
            even though they mean opposite things and offer different
            remedies (contact support here; nothing to do there).
            "Deactivated" also matches the body copy and the prefilled
            WhatsApp message just above, which both already say it. */}
        <h2 style={{ marginBottom: '12px' }}>This card has been deactivated</h2>
        <p style={{ maxWidth: '420px', marginBottom: '28px', color: '#5c4a1f' }}>
          This Digital Contact Card is no longer active. Contact support and
          we'll help get it sorted.
        </p>
        <a
          href={buildSupportWhatsAppUrl(supportMessage)}
          target="_blank"
          rel="noopener noreferrer"
          className="btn"
          style={{
            backgroundColor: '#25D366',
            color: 'white',
            padding: '12px 28px',
            borderRadius: '6px',
            textDecoration: 'none',
          }}
        >
          Contact support on WhatsApp
        </a>
      </div>
    );
  }

  if (gateReason === 'notActivated') {
    // Nothing here for a stranger scanning this card to do -- only the
    // owner can fix this, from their own dashboard's activation banner
    // (see Dashboard.js). No support CTA: this isn't an account issue
    // support needs to intervene on, just a card that isn't set up yet.
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          padding: '40px 24px',
          backgroundColor: '#eef3fb',
        }}
      >
        <h2 style={{ marginBottom: '12px' }}>This card isn't active yet</h2>
        <p style={{ maxWidth: '420px', color: '#33415c' }}>
          The owner hasn't finished setting up this Digital Contact Card.
          Check back soon.
        </p>
      </div>
    );
  }

  if (logedUser !== null && id === logedUser.userName)
    return <Redirect to="/login" />;

  if (loading || !user) return <Spinner />;

  // Payment methods render below the links -- at display time only, so
  // the user's stored `order` is never rewritten. Sorted before
  // computeSocialDisplay because that resolves names by position.
  const orderedEntries = sortEntriesForDisplay(user.socialList);
  const socialDisplay = computeSocialDisplay(orderedEntries);
  const socialItems = orderedEntries.map((entry, index) => ({
    entry,
    ...socialDisplay[index],
  }));

  return (
    <div>
      <div className="my-profile">
        <div className="container" style={{ padding: '0', overflow: 'hidden' }}>
          <div
            style={{
              backgroundColor: 'black',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px 10px',
            }}
          >
            <div>
              {isAuth ? (
                <Link to="/login" style={{ color: 'white' }}>
                  Profile
                </Link>
              ) : (
                <Link to="/login" style={{ color: 'white' }}>
                  Login
                </Link>
              )}
            </div>
            <div
              style={{ backgroundColor: 'white', padding: '1px' }}
              onClick={() => setshow('show')}
            >
              <img
                src="https://firebasestorage.googleapis.com/v0/b/digitalcontacts-2699b.appspot.com/o/images%2Fqr-code.svg?alt=media&token=ed5b1895-ce13-42ff-abb6-605d5313f6bf"
                width="25"
                className="showPopup"
                target="#profileQrCon"
              />
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <div
                className="col-12 cover"
                style={{
                  backgroundImage: user.coverUrl
                    ? `url(${user.coverUrl})`
                    : `url(${DEFAULT_COVER})`,
                }}
              >
                <div className="wrapper">
                  <div className="my-profile-photo">
                    <img
                      src={user.avatarUrl || DEFAULT_AVATAR}
                      alt="photo"
                      id="profileImg"
                      onError={handleAvatarError}
                    />
                  </div>
                </div>
              </div>
              <h1 id="name">{user.name}</h1>

              <p id="bio" style={{ whiteSpace: 'pre-line' }}>
                {user.bio}
              </p>
              <div className="d-flex" style={{ padding: '0 15px' }}>
                <div
                  className="col-12"
                  id="btnDownloadVcard"
                  style={{ padding: 0 }}
                >
                  <a
                    href={`${window.location.origin}/api/users/vcf/${user.lengthId}`}
                    download
                    className="btn"
                  >
                    Add to Contacts
                  </a>
                </div>
              </div>

              <p style={{ textAlign: 'center', margin: '2px 0 20px' }}>
                <span
                  onClick={() => setShowLeadModal(true)}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') setShowLeadModal(true);
                  }}
                  style={{
                    display: 'inline-block',
                    padding: '12px 18px',
                    color: '#0000EE',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    fontSize: '15px',
                  }}
                >
                  Share your details with me
                </span>
              </p>

              <b className="text-center mt-2 mb-2 d-block">
                <div className="col-12 social2" style={{ padding: '0 15px' }}>
                  <ul className="row">
                    {socialItems.map(({ entry, name, subtitle }, index) => {
                      const key = entry._id || `${entry.platform}-${index}`;
                      // kind comes from the server's public allow-list,
                      // derived from the platform registry -- not read
                      // off the stored row, so it can't disagree with
                      // what the platform actually is.
                      return entry.kind === 'payment' ? (
                        <PaymentListItem
                          key={key}
                          entry={entry}
                          displayName={name}
                          subtitle={subtitle}
                          handleClicks={handleClicks}
                          isPublic={true}
                          profileKey={user.lengthId}
                          isOpen={isPaymentOpen(key)}
                          onToggle={() => togglePayment(key)}
                          size={'18px'}
                        />
                      ) : (
                        <ListItem
                          key={key}
                          entry={entry}
                          displayName={name}
                          subtitle={subtitle}
                          handleClicks={handleClicks}
                          isPublic={true}
                          size={'18px'}
                        />
                      );
                    })}
                  </ul>

                </div>
              </b>
            </div>
            <b className="text-center mt-2 mb-2 d-block"></b>
          </div>
          <b className="text-center mt-2 mb-2 d-block"></b>
        </div>
        <b className="text-center mt-2 mb-2 d-block"></b>
      </div>
      <Footer />

      <QrScan user={user} show={show} setshow={setshow} />

      {showLeadModal && (
        <LeadCaptureModal
          lengthId={user.lengthId}
          ownerName={user.name}
          onClose={() => setShowLeadModal(false)}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  authh: state.registerUser,
  logedUser: state.registerUser.user,
});

export default connect(mapStateToProps, { logout })(PublicProfile);
